/* eslint-disable no-console */
import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import React, { useEffect, useState } from 'react';
import { companyIdAtom } from 'views/state';

import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  useOnFilterChangeResetAtoms,
  useRepositoryListFilterValue,
} from './state';
import {
  getEnumKeyByEnumValue,
  getHostLabel,
  HostType,
  RepositoryFilterOptions,
} from '../helper';
import { RespositoryStatusOptions } from './helpers';

export const useFilterConfig = (): FilterConfig => {
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const companyId = useAtomValue(companyIdAtom);
  const [accountsTitles, setAccountTitles] = useState<
    { label: string; value: string; host_type: string }[]
  >([]);

  const resetOnFilterChange = useOnFilterChangeResetAtoms();
  const selectedHostTypes =
    useRepositoryListFilterValue<string[]>('host_types');

  useEffect(() => {
    const fetchAccountTitles = async () => {
      try {
        if (companyId && companyId !== -1) {
          const params = {
            company_id: companyId,
          };

          const responseData = await callApi(
            'sast/accounts/titles/',
            'GET',
            params,
          );
          const accountsTitles =
            responseData?.data.map((item) => ({
              label: item.title,
              value: String(item.id),
              host_type: item.host_type,
            })) || [];
          console.log('accountsTitles', accountsTitles);

          setAccountTitles(accountsTitles);
          return;
        }
      } catch (error) {
        console.error(error);
        setAccountTitles([]);
      }
    };
    void fetchAccountTitles();
  }, [companyId]);
  useEffect(() => {
    if (accountsTitles.length === 0) return;
    setFilterValues((prev) => {
      const selectedAccounts = prev.account_ids || [''];

      const validAccounts = accountsTitles
        .filter(
          (account) =>
            selectedAccounts.includes(account.value) &&
            (selectedHostTypes.length === 0 ||
              selectedHostTypes.includes(account.host_type)),
        )
        .map((account) => account.value);

      const newAccountIds = validAccounts.length > 0 ? validAccounts : [''];

      if (
        selectedAccounts.length === newAccountIds.length &&
        selectedAccounts.every((v) => newAccountIds.includes(v))
      ) {
        return prev;
      }

      return { ...prev, account_ids: newAccountIds };
    });
  }, [selectedHostTypes, accountsTitles, setFilterValues]);
  const filteredAccounts = React.useMemo(() => {
    if (!selectedHostTypes.length || selectedHostTypes[0] === '') {
      return accountsTitles;
    }
    return accountsTitles.filter((account) =>
      selectedHostTypes.includes(account.host_type),
    );
  }, [accountsTitles, selectedHostTypes]);

  useEffect(() => {
    setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {
      const params = new URLSearchParams(window.location.search);
      const accountIdParam = params.get('account_id');
      if (accountIdParam) {
        queryFilters.account_ids = [parseInt(accountIdParam)];
      }
    };

    void initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          resetOnFilterChange();
          setFilterValues(queryFilters);
        }
      })
      .finally(() => {
        setQuerystringFiltersInitialized(true);
      });

    return () => {
      setQuerystringFiltersInitialized(false);
    };
  }, []);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: ['host_types', 'state_filters', 'status'],
      leftPanelFilters: ['account_ids', 'dateRangeFilters'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search Repositories',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'host_types',
          label: 'Host Types',
          placeholder: 'Select Host Types',
          tooltip: (
            <div>
              <p>Filter repositories by Host Type (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-code-branch',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...Object.values(HostType).map((host) => ({
              value: host,
              label: getHostLabel(host),
            })),
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'account_ids',
          label: 'Accounts',
          placeholder: 'Select Accounts',
          tooltip: (
            <div>
              <p>Filter repositories by Accounts (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-user-tie',
          defaultSelectedValues: [''],
          enabled: filteredAccounts.length > 0,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...filteredAccounts,
          ],
          renderStyle: MultiSelectRenderStyle.Dropdown,
        }),
        new MultiSelectFilterConfig({
          id: 'status',
          label: 'Repository Status',
          placeholder: 'Select Status',
          tooltip: (
            <div>
              <p>Filter repositories by Scan Status (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...Object.values(RespositoryStatusOptions).map((value) => ({
              value: value, // Use enum key as value
              label: getEnumKeyByEnumValue(RespositoryStatusOptions, value),
            })),
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'state_filters',
          label: 'State',
          placeholder: 'Select State',
          tooltip: (
            <div>
              <p>Filter repositories by State (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-tasks',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...Object.values(RepositoryFilterOptions).map((value) => ({
              value: value, // Use enum key as value
              label: getEnumKeyByEnumValue(RepositoryFilterOptions, value),
            })),
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created Date',
              labelWidth: '145px',
            }),
            new DateRangeFilterConfig({
              id: 'updated_at',
              label: 'Updated Date',
              labelWidth: '145px',
            }),
            new DateRangeFilterConfig({
              id: 'last_commit_date',
              label: 'Code Updated Date',
              labelWidth: '145px',
            }),
            new DateRangeFilterConfig({
              id: 'reactivated_at',
              label: 'Last Reactivated Date',
              labelWidth: '145px',
            }),
            new DateRangeFilterConfig({
              id: 'deactivated_at',
              label: 'Last Deactivated Date',
              labelWidth: '145px',
            }),
          ],
        }),
      ],
    });

    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, filteredAccounts]);

  return config;
};
